import { Zap, RefreshCw, Clock } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";

/**
 * Brokers section under the Integrations card: two columns describing how
 * trades get into Edgely. Left = manual/CSV import (live), right = automatic
 * broker sync (Tradovate in beta, MetaTrader coming soon). Logos are white/colour-on-
 * transparent marks designed for the dark surface, so they sit directly on the
 * section background inside subtle cards.
 */

interface BrokerLogo {
  name: string;
  src: string;
  badge?: "soon" | "beta";
}

const IMPORT_BROKERS: BrokerLogo[] = [
  { name: "Tradovate", src: "/logos/tradovate.png" },
  { name: "TopstepX", src: "/logos/topstepx.png" },
  { name: "Quantower", src: "/logos/quantower.png" },
];

// Auto-sync column: Tradovate sync ships in beta, MetaTrader is coming soon.
const SYNC_BROKERS: BrokerLogo[] = [
  { name: "Tradovate", src: "/logos/tradovate.png", badge: "beta" },
  { name: "MetaTrader 4 & 5", src: "/logos/metatrader.png", badge: "soon" },
];

function LogoTile({ broker }: { broker: BrokerLogo }) {
  const t = useTranslation();
  return (
    <div className="relative flex h-20 items-center justify-center rounded-xl border border-border/40 bg-card/60 px-6 backdrop-blur-sm transition-colors duration-200 hover:border-border/80">
      <img
        src={broker.src}
        alt={broker.name}
        loading="lazy"
        className="max-h-9 w-auto max-w-[150px] object-contain"
      />
      {broker.badge === "soon" && (
        <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full border border-[#EF9F27]/40 bg-[#EF9F27]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#EF9F27]">
          <Clock className="h-2.5 w-2.5" />
          {t.common.soon}
        </span>
      )}
      {broker.badge === "beta" && (
        <span className="absolute right-2 top-2 inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
          {t.common.beta}
        </span>
      )}
    </div>
  );
}

export default function BrokersSection() {
  const t = useTranslation();

  return (
    <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-x-16 lg:gap-x-24">
      {/* Column 1 — 10-second import */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Zap className="h-4 w-4" />
          </span>
          <h3 className="text-sm font-semibold text-foreground">
            {t.landing.freePlan.brokersImportTitle}
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-3">
          {IMPORT_BROKERS.map((broker) => (
            <LogoTile key={broker.name} broker={broker} />
          ))}
        </div>
      </div>

      {/* Column 2 — Automatic sync */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <RefreshCw className="h-4 w-4" />
          </span>
          <h3 className="text-sm font-semibold text-foreground">
            {t.landing.freePlan.brokersSyncTitle}
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-3">
          {SYNC_BROKERS.map((broker) => (
            <LogoTile key={broker.name} broker={broker} />
          ))}
        </div>
      </div>
    </div>
  );
}
